import { ReactNode } from 'react';
import { TaskCompletionCardItem, WorkflowKey } from '@/apis/business/agent/performance/types';
import AppointmentsCancelledIcon from '@/assets/icons/AppointmentsCancelledIcon';
import AppointmentsCreatedIcon from '@/assets/icons/AppointmentsCreatedIcon';
import AppointmentsRescheduleIcon from '@/assets/icons/AppointmentsRescheduledIcon';
import CreateEstimateRequestIcon from '@/assets/icons/CreateEstimateRequestIcon';
import CreateJobIcon from '@/assets/icons/CreateJobIcon';
import CreateOrderIcon from '@/assets/icons/CreateOrderIcon';
import CreateServiceRequestIcon from '@/assets/icons/CreateServiceRequestIcon';
import NotesTakenIcon from '@/assets/icons/NotesTakenIcon';
import PatientCreatedIcon from '@/assets/icons/PatientCreatedIcon';
import RefillRequests from '@/assets/icons/RefillRequests';
import TaskIcon from '@/assets/icons/TaskIcon';

const WRITE_TO_WORKSHEET = 'write_to_worksheet';

/** Past-tense / noun-first labels + icons keyed by tool name. */
export const WORKFLOW_CONFIG: Record<WorkflowKey, Pick<TaskCompletionCardItem, 'label' | 'icon'>> = {
  notes_taken: {
    label: 'Notes Taken',
    icon: <NotesTakenIcon />
  },
  write_to_worksheet: {
    label: 'Notes Taken',
    icon: <NotesTakenIcon />
  },
  write_to_note_taking: {
    label: 'Notes Taken',
    icon: <NotesTakenIcon />
  },
  create_appointment: {
    label: 'Appointments Created',
    icon: <AppointmentsCreatedIcon />
  },
  create_direct_appointment: {
    label: 'Appointments Created',
    icon: <AppointmentsCreatedIcon />
  },
  cancel_appointment: {
    label: 'Appointments Cancelled',
    icon: <AppointmentsCancelledIcon />
  },
  update_appointment: {
    label: 'Appointments Rescheduled',
    icon: <AppointmentsRescheduleIcon />
  },
  request_refill: {
    label: 'Refill Requests',
    icon: <RefillRequests />
  },
  create_patient: {
    label: 'Patient Created',
    icon: <PatientCreatedIcon />
  },
  create_event: {
    label: 'Events Created',
    icon: <AppointmentsCreatedIcon />
  },
  cancel_event: {
    label: 'Events Cancelled',
    icon: <AppointmentsCancelledIcon />
  },
  update_event: {
    label: 'Events Rescheduled',
    icon: <AppointmentsRescheduleIcon />
  },
  create_client: {
    label: 'Client Created',
    icon: <PatientCreatedIcon />
  },
  create_customer: {
    label: 'Customer Created',
    icon: <PatientCreatedIcon />
  },
  create_contact: {
    label: 'Contact Created',
    icon: <PatientCreatedIcon />
  },
  create_service_request: {
    label: 'Service Requests Created',
    icon: <CreateServiceRequestIcon />
  },
  create_estimate_request: {
    label: 'Estimate Requests Created',
    icon: <CreateEstimateRequestIcon />
  },
  create_job: {
    label: 'Jobs Created',
    icon: <CreateJobIcon />
  },
  create_order: {
    label: 'Orders Created',
    icon: <CreateOrderIcon />
  }
};

/**
 * Icon fallback by owning state type when tool name is unknown.
 * `order_taking` uses CreateOrderIcon until the Figma food icon lands.
 */
export const TYPE_ICON_CONFIG: Record<string, ReactNode> = {
  note_taking: <NotesTakenIcon />,
  task: <TaskIcon width={24} height={24} />,
  order_taking: <CreateOrderIcon />
};

function titleizeToolName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Prefer tool name config, then worksheet executor / state type icon. */
export function resolveTaskCompletionIcon(
  name: string,
  type?: string | null,
  executorLabel?: string | null
): ReactNode {
  const byName = WORKFLOW_CONFIG[name]?.icon;
  if (byName) return byName;

  if (executorLabel === WRITE_TO_WORKSHEET) return <NotesTakenIcon />;
  if (type && TYPE_ICON_CONFIG[type]) return TYPE_ICON_CONFIG[type];

  return null;
}

/**
 * Prefer known tool-name label; worksheet titles (human names from API) stay as-is
 * when they are not snake_case tool keys in WORKFLOW_CONFIG.
 */
export function resolveTaskCompletionLabel(
  name: string,
  worksheetName?: string | null,
  executorLabel?: string | null
): string {
  if (worksheetName) return worksheetName;
  if (executorLabel === WRITE_TO_WORKSHEET && name && !WORKFLOW_CONFIG[name]) {
    return name;
  }

  return WORKFLOW_CONFIG[name]?.label || titleizeToolName(name);
}

export function resolveTaskCompletionConfig(
  name: string,
  type?: string | null,
  executorLabel?: string | null,
  worksheetName?: string | null
): Pick<TaskCompletionCardItem, 'label' | 'icon'> {
  return {
    label: resolveTaskCompletionLabel(name, worksheetName, executorLabel),
    icon: resolveTaskCompletionIcon(name, type, executorLabel)
  };
}

export function formatTaskCompletionLabel(
  name: string,
  type: string | null | undefined,
  showType: boolean,
  worksheetName?: string | null,
  executorLabel?: string | null
): string {
  const baseLabel = resolveTaskCompletionLabel(name, worksheetName, executorLabel);
  if (!showType || !type) return baseLabel;

  const typeLabel = type.replace(/_/g, ' ');
  return `${baseLabel} (${typeLabel})`;
}
